// src/components/Coupons/CreateCoupon.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Snackbar,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { createCoupon, updateCoupon } from "../../Apis/CouponAPI";

const CreateCoupon = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const editMode = Boolean(id);

    const location = useLocation();
    const couponData = location.state?.coupon || null;

    const [couponCode, setCouponCode] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [discountType, setDiscountType] = useState("percentage");
    const [discountValue, setDiscountValue] = useState("");
    const [minAmount, setMinAmount] = useState("");
    const [maxDiscountAmount, setMaxDiscountAmount] = useState("");
    const [usageLimit, setUsageLimit] = useState("");
    const [validFrom, setValidFrom] = useState("");
    const [validTo, setValidTo] = useState("");
    const [status, setStatus] = useState(true);
    const [couponImages, setCouponImages] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // Fill state in edit mode
    useEffect(() => {
        if (editMode && couponData) {
            setCouponCode(couponData.couponCode || "");
            setTitle(couponData.title || "");
            setDescription(couponData.description || "");
            setDiscountType(couponData.discountType || "percentage");
            setDiscountValue(couponData.discountValue || "");
            setMinAmount(couponData.minAmount || "");
            setMaxDiscountAmount(couponData.maxDiscountAmount || "");
            setUsageLimit(couponData.usageLimit || "");
            setValidFrom(
                couponData.validFrom
                    ? new Date(couponData.validFrom).toISOString().split("T")[0]
                    : ""
            );
            setValidTo(
                couponData.validTo
                    ? new Date(couponData.validTo).toISOString().split("T")[0]
                    : ""
            );
            setStatus(couponData.status ?? true);

            // Image preview
            let imageUrl = null;
            if (couponData.couponImage) {
                imageUrl = couponData.couponImage; // single image
            } else if (couponData.couponImages && couponData.couponImages.length > 0) {
                imageUrl = couponData.couponImages[0]; // take first one
            }
            setPreview(imageUrl);
        }
    }, [editMode, couponData]);

    // Image input handler
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        setCouponImages(file);
        if (file) {
            setPreview(URL.createObjectURL(file));
        }
    };

    // Form submit handler
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Required fields validation
        if (
            !couponCode ||
            !title ||
            !description ||
            !discountType ||
            !discountValue ||
            !minAmount ||
            !validFrom ||
            !validTo
        ) {
            setErrorMsg("Please fill all required fields.");
            return;
        }

        const formData = new FormData();
        formData.append("couponCode", couponCode);
        formData.append("title", title);
        formData.append("description", description);
        formData.append("discountType", discountType);
        formData.append("discountValue", discountValue || 0);
        formData.append("minAmount", minAmount || 0);
        formData.append("maxDiscountAmount", maxDiscountAmount || 0);
        formData.append("usageLimit", usageLimit || 0);
        formData.append("validFrom", validFrom);
        formData.append("validTo", validTo);
        formData.append("status", status ? "true" : "false");

        if (couponImages) {
            formData.append("couponImages", couponImages);
        }

        try {
            setLoading(true);
            if (editMode) {
                await updateCoupon(id, formData);
            } else {
                await createCoupon(formData);
            }
            setSuccess(true);
            setTimeout(() => navigate("/getadmincoupons"), 1200);
        } catch (err) {
            console.error("Coupon error:", err);
            setErrorMsg(editMode ? "Failed to update coupon." : "Failed to create coupon.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                px: 3,
                py: 4,
            }}
        >
            <Paper sx={{ width: "60%", p: 4, borderRadius: 3, boxShadow: 6 }}>
                <Typography variant="h5" gutterBottom>
                    {editMode ? "Update Coupon" : "Create Coupon"}
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                        <TextField
                            label="Coupon Code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            size="small"
                            multiline
                            rows={3}
                            sx={{ flex: "1 1 100%" }}
                        />

                        <FormControl size="small" sx={{ flex: "1 1 48%" }}>
                            <InputLabel>Discount Type</InputLabel>
                            <Select
                                value={discountType}
                                onChange={(e) => setDiscountType(e.target.value)}
                                label="Discount Type"
                            >
                                <MenuItem value="percentage">Percentage</MenuItem>
                                <MenuItem value="fixed">Fixed</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            label="Discount Value"
                            value={discountValue}
                            onChange={(e) => setDiscountValue(e.target.value)}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Minimum Amount"
                            value={minAmount}
                            onChange={(e) => setMinAmount(e.target.value)}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Maximum Discount Amount"
                            value={maxDiscountAmount}
                            onChange={(e) => setMaxDiscountAmount(e.target.value)}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Usage Limit"
                            value={usageLimit}
                            onChange={(e) => setUsageLimit(e.target.value)}
                            size="small"
                            sx={{ flex: "1 1 48%" }}
                        />

                        <TextField
                            label="Valid From"
                            type="date"
                            value={validFrom}
                            onChange={(e) => setValidFrom(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: "1 1 48%" }}
                        />
                        <TextField
                            label="Valid To"
                            type="date"
                            value={validTo}
                            onChange={(e) => setValidTo(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: "1 1 48%" }}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={status}
                                    onChange={(e) => setStatus(e.target.checked)}
                                />
                            }
                            label="Active"
                        />
                    </Box>

                    <Button variant="contained" component="label" sx={{ mt: 2 }}>
                        Upload Image
                        <input type="file" hidden onChange={handleImageChange} />
                    </Button>

                    {preview && (
                        <Box sx={{ mt: 2, textAlign: "center" }}>
                            <Typography variant="body2">Image Preview:</Typography>
                            <img
                                src={preview}
                                alt="preview"
                                style={{ width: 150, marginTop: 8, borderRadius: 8 }}
                            />
                        </Box>
                    )}

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ mt: 3 }}
                        disabled={loading}
                    >
                        {loading
                            ? editMode
                                ? "Updating..."
                                : "Creating..."
                            : editMode
                                ? "Update Coupon"
                                : "Create Coupon"}
                    </Button>
                </form>

                {/* Success Snackbar */}
                <Snackbar
                    open={success}
                    autoHideDuration={3000}
                    onClose={() => setSuccess(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        severity="success"
                        variant="filled"
                        onClose={() => setSuccess(false)}
                    >
                        {editMode
                            ? "Coupon updated successfully!"
                            : "Coupon created successfully!"}
                    </Alert>
                </Snackbar>

                {/* Error Snackbar */}
                <Snackbar
                    open={!!errorMsg}
                    autoHideDuration={4000}
                    onClose={() => setErrorMsg("")}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        severity="error"
                        variant="filled"
                        onClose={() => setErrorMsg("")}
                    >
                        {errorMsg}
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
};

export default CreateCoupon;
